import { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckCircle, AlertCircle, ScanLine, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Kiosk — public unattended check-in screen at /kiosk. Gated by a 4-digit PIN
 * that admins configure in Settings. After a successful scan/entry it flashes
 * the result for 2s then resets; missing IDs show a full-screen red banner
 * with the "Please Sit at Table 1" copy.
 */
export default function Kiosk() {
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [scanInput, setScanInput] = useState("");
  const [result, setResult] = useState(null); // { found, student_name, message }
  const [pending, setPending] = useState(false);
  const scanInputRef = useRef(null);
  const resetTimerRef = useRef(null);

  const verifyPin = async () => {
    setPinError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/kiosk/verify-pin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setPinError(err.detail || "Incorrect PIN");
        return;
      }
      setUnlocked(true);
      setTimeout(() => scanInputRef.current?.focus(), 50);
    } catch (e) {
      setPinError(e.message || "Unable to reach server");
    }
  };

  const submitScan = async (value) => {
    if (!value || pending) return;
    setPending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/kiosk/scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, other_id: value }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResult({ found: false, message: data.detail || `HTTP ${res.status}` });
      } else {
        setResult(data);
      }
    } catch (e) {
      setResult({ found: false, message: e.message || "Scan failed" });
    } finally {
      setPending(false);
      setScanInput("");
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => {
        setResult(null);
        setTimeout(() => scanInputRef.current?.focus(), 30);
      }, 2000);
    }
  };

  // Auto-submit at exactly 6 digits (mirrors the attendance scan behavior)
  useEffect(() => {
    if (!unlocked) return;
    const trimmed = scanInput.trim();
    if (!/^\d{6}$/.test(trimmed)) return;
    if (pending) return;
    const t = setTimeout(() => submitScan(trimmed), 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanInput, unlocked, pending]);

  useEffect(() => () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
  }, []);

  // Keep the scan input focused when idle
  useEffect(() => {
    if (unlocked && !result && !pending) {
      scanInputRef.current?.focus();
    }
  }, [unlocked, result, pending]);

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6" data-testid="kiosk-pin-screen">
        <Card className="w-full max-w-sm border-none shadow-2xl">
          <CardContent className="p-8 space-y-4">
            <div className="flex flex-col items-center text-center">
              <Lock className="w-10 h-10 text-slate-700 mb-2" />
              <h1 className="text-2xl font-bold text-slate-900">Kiosk Access</h1>
              <p className="text-sm text-slate-500 mt-1">Enter the 4-digit PIN to continue</p>
            </div>
            <Input
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              onKeyDown={(e) => { if (e.key === "Enter") verifyPin(); }}
              placeholder="••••"
              className="h-14 text-3xl text-center font-mono tracking-widest"
              type="password"
              inputMode="numeric"
              maxLength={4}
              autoFocus
              data-testid="kiosk-pin-input"
            />
            {pinError && (
              <p className="text-sm text-red-700 text-center" data-testid="kiosk-pin-error">
                {pinError}
              </p>
            )}
            <Button
              onClick={verifyPin}
              disabled={pin.length !== 4}
              className="w-full h-12 bg-slate-800 hover:bg-slate-900"
              data-testid="kiosk-pin-submit"
            >
              Unlock
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Full-screen "Not Found" banner
  if (result && !result.found) {
    return (
      <div
        className="min-h-screen bg-red-700 text-white flex items-center justify-center p-6"
        data-testid="kiosk-not-found"
      >
        <div className="max-w-3xl text-center">
          <AlertCircle className="w-24 h-24 mx-auto mb-6" strokeWidth={2.5} />
          <p className="text-5xl font-extrabold tracking-tight mb-4 uppercase">Not Found</p>
          <p className="text-2xl leading-relaxed">
            Please Sit at Table 1 (First Table) for Assistance.
          </p>
        </div>
      </div>
    );
  }

  // Success flash
  if (result && result.found) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-green-500 to-emerald-700 text-white flex items-center justify-center p-6"
        data-testid="kiosk-welcome"
      >
        <div className="max-w-3xl text-center">
          <CheckCircle className="w-24 h-24 mx-auto mb-6" strokeWidth={2.5} />
          <p className="text-5xl font-extrabold tracking-tight mb-4">
            Welcome, {result.student_name}!
          </p>
          <p className="text-xl opacity-90 font-mono">ID {result.other_id} · Marked Present</p>
        </div>
      </div>
    );
  }

  // Ready state — scan input
  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-6"
      data-testid="kiosk-ready"
    >
      <Card className="w-full max-w-lg border-none shadow-2xl">
        <CardContent className="p-10 space-y-6 text-center">
          <ScanLine className="w-16 h-16 mx-auto text-blue-600 animate-pulse" />
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Scan Your Badge</h1>
            <p className="text-slate-500 mt-2">or type your 6-digit ID and press Enter</p>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitScan(scanInput.trim());
            }}
          >
            <Input
              ref={scanInputRef}
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              onBlur={() => setTimeout(() => scanInputRef.current?.focus(), 100)}
              placeholder="000000"
              className="h-16 text-2xl text-center font-mono"
              disabled={pending}
              autoFocus
              data-testid="kiosk-scan-input"
            />
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
